import React, { useEffect } from 'react';
import { Navbar } from '../../components/feature/Navbar';
import { Footer } from '../../components/feature/Footer';
import { AISearchOptimization } from '../../components/feature/AISearchOptimization';

const ServicesPage: React.FC = () => {
  useEffect(() => {
    // Update page title and meta tags
    document.title = 'Web Design Services | BusinessWebsitePro';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Custom web design, SEO, e-commerce, and maintenance services in Vancouver. Transparent pricing with no hidden fees.');
    }

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Full-Stack Website Design & ASO Services | Transparent Pricing, Guaranteed Ownership | BWP');
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Professional full-stack website design, SEO, and ASO services with transparent flat-rate pricing. Get guaranteed full ownership of your website with no hidden fees.');
    }

    const twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', 'Full-Stack Website Design & ASO Services | Transparent Pricing, Guaranteed Ownership | BWP');
    }

    const twitterDescription = document.querySelector('meta[property="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', 'Professional full-stack website design, SEO, and ASO services with transparent flat-rate pricing. Get guaranteed full ownership of your website with no hidden fees.');
    }
  }, []);

  // Add Google Fonts using useEffect
  useEffect(() => {
    // Add preconnect links
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);

    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);

    // Add font stylesheet
    const fontLink = document.createElement('link');
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@700;800&family=Dancing+Script:wght@400;700&display=swap';
    fontLink.rel = 'stylesheet';
    document.head.appendChild(fontLink);

    return () => {
      // Cleanup on unmount
      document.head.removeChild(preconnect1);
      document.head.removeChild(preconnect2);
      document.head.removeChild(fontLink);
    };
  }, []);

  const packages = [
    {
      title: 'Starter Website',
      pages: '1–3 Pages',
      description: 'Ideal for new businesses or startups that need a clean, simple online presence.',
      features: [
        'Modern layout design',
        'Mobile-friendly responsive',
        'Essential business information',
        'Professional presentation',
        'Contact form integration',
        'Fast loading speed'
      ],
      icon: 'ri-rocket-line'
    },
    {
      title: 'Business Website',
      pages: '3–5 Pages',
      description: 'Perfect for growing companies that need more space to showcase their services and brand story.',
      features: [
        'Multi-page structure',
        'Enhanced visual design',
        'Branding sections',
        'Stronger SEO foundations',
        'Service showcase pages',
        'Professional imagery'
      ],
      icon: 'ri-building-line',
      popular: true
    },
    {
      title: 'Premium Website',
      pages: '5–10 Pages',
      description: 'Designed for established businesses that want a complete, high-impact website with advanced layouts.',
      features: [
        'Detailed service sections',
        'Visual galleries',
        'Blog setup included',
        'Custom design feel',
        'Advanced functionality',
        'Complete brand experience'
      ],
      icon: 'ri-vip-crown-line'
    }
  ];

  const included = [
    { icon: 'ri-smartphone-line', title: 'Mobile-Friendly Design', description: 'Responsive design that works perfectly on all devices' },
    { icon: 'ri-layout-line', title: 'Modern Professional Layouts', description: 'Tailored to your industry with contemporary aesthetics' },
    { icon: 'ri-search-line', title: 'Basic SEO Setup', description: 'Optimized for better visibility on search engines' },
    { icon: 'ri-mail-send-line', title: 'Contact Forms & CTAs', description: 'Capture leads with strategic call-to-action sections' },
    { icon: 'ri-map-pin-line', title: 'Google Maps Integration', description: 'Help customers find your location easily' },
    { icon: 'ri-share-line', title: 'Social Media Integration', description: 'Connect all your social platforms seamlessly' },
    { icon: 'ri-flashlight-line', title: 'Lightning-Fast Loading', description: 'Optimized performance and security built-in' },
    { icon: 'ri-image-line', title: 'Image Optimization', description: 'Clean, high-quality visuals that load quickly' }
  ];

  const process = [
    { 
      number: '01', 
      title: 'Discovery & Planning', 
      description: 'We learn about your business, your goals, and the style you want. You share your content, images, and brand details.',
      icon: 'ri-compass-3-line'
    },
    { 
      number: '02', 
      title: 'Design & Layout Creation', 
      description: 'We create a clean, modern website layout that reflects your brand and presents your information professionally.',
      icon: 'ri-palette-line'
    },
    { 
      number: '03', 
      title: 'Build & Development', 
      description: 'Your website pages are built with optimized content, visuals, structure, and mobile responsiveness.',
      icon: 'ri-code-s-slash-line'
    },
    { 
      number: '04', 
      title: 'Review & Revisions', 
      description: 'You review the website and request changes. We refine the design until you\'re fully satisfied.',
      icon: 'ri-eye-line'
    },
    { 
      number: '05', 
      title: 'Launch', 
      description: 'Your website goes live on your domain, ready for customers to visit.',
      icon: 'ri-rocket-2-line'
    }
  ];

  const industries = [
    { name: 'Restaurants & Cafés', icon: 'ri-restaurant-line' },
    { name: 'Salons & Beauty Services', icon: 'ri-scissors-cut-line' },
    { name: 'Trades & Home Services', icon: 'ri-tools-line' },
    { name: 'Consultants & Coaches', icon: 'ri-briefcase-line' },
    { name: 'Retail & Boutiques', icon: 'ri-shopping-bag-line' },
    { name: 'E-commerce Shops', icon: 'ri-shopping-cart-line' },
    { name: 'Real Estate Professionals', icon: 'ri-home-4-line' },
    { name: 'Accounting & Financial', icon: 'ri-line-chart-line' },
    { name: 'Local Services & Startups', icon: 'ri-store-2-line' }
  ];

  const reasons = [
    { icon: 'ri-medal-line', title: 'Professional Quality', description: 'Small-business pricing without compromising on quality' },
    { icon: 'ri-time-line', title: 'Fast Delivery', description: 'Modern design standards with quick turnaround' },
    { icon: 'ri-message-3-line', title: 'Clear Communication', description: 'Easy onboarding and transparent process' },
    { icon: 'ri-search-eye-line', title: 'SEO Optimized', description: 'Built for both users and search engines' },
    { icon: 'ri-shield-check-line', title: 'Trusted Partner', description: 'Serving businesses worldwide' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Breadcrumb Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://businesswebsitepro.com/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Services",
              "item": "https://businesswebsitepro.com/services"
            }
          ]
        })}
      </script>

      {/* Service Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Professional Business Website Design",
          "name": "Professional Business Website Design Services",
          "description": "Custom, mobile-responsive website design with transparent flat-rate pricing, guaranteed full ownership, and zero hidden fees. Built for maximum SEO and AI Search Optimization (ASO) visibility.",
          "provider": {
            "@id": "https://businesswebsitepro.com/#organization"
          },
          "areaServed": [
            {
              "@type": "State",
              "name": "British Columbia",
              "containedIn": {
                "@type": "Country",
                "name": "Canada"
              }
            },
            {
              "@type": "Country",
              "name": "Canada"
            },
            {
              "@type": "Country",
              "name": "United States"
            }
          ],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Website Design and Digital Marketing Packages",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Website Design Package"
                },
                "priceSpecification": {
                  "@type": "PriceSpecification",
                  "minPrice": "399",
                  "priceCurrency": "USD"
                }
              }
            ]
          },
          "offers": {
            "@type": "AggregateOffer",
            "lowPrice": "399",
            "priceCurrency": "USD",
            "name": "Transparent Flat-Rate Website Packages"
          }
        })}
      </script>

      {/* ItemList Schema - Existing */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "itemListElement": [
            {
              "@type": "Service",
              "name": "Starter Website Design",
              "description": "1-3 page website ideal for new businesses with modern layout, mobile-friendly responsive design, and professional presentation",
              "provider": {
                "@type": "ProfessionalService",
                "name": "BusinessWebsitePro"
              },
              "areaServed": "Worldwide",
              "offers": {
                "@type": "Offer",
                "priceCurrency": "USD",
                "price": "399"
              }
            },
            {
              "@type": "Service",
              "name": "Business Website Design",
              "description": "3-5 page website perfect for growing companies with enhanced visual design, branding sections, and stronger SEO foundations",
              "provider": {
                "@type": "ProfessionalService",
                "name": "BusinessWebsitePro"
              },
              "areaServed": "Worldwide",
              "offers": {
                "@type": "Offer",
                "priceCurrency": "USD",
                "price": "599"
              }
            },
            {
              "@type": "Service",
              "name": "Premium Website Design",
              "description": "5-10 page website designed for established businesses with detailed service sections, visual galleries, blog setup, and advanced functionality",
              "provider": {
                "@type": "ProfessionalService",
                "name": "BusinessWebsitePro"
              },
              "areaServed": "Worldwide",
              "offers": {
                "@type": "Offer",
                "priceCurrency": "USD",
                "price": "999"
              }
            }
          ]
        })}
      </script>
      
      {/* SEO H1 - Hidden but present */}
      <header className="sr-only">
        <h1>Professional Website Design Services for Small Businesses in British Columbia, Canada, and Worldwide</h1>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-[75vh] max-h-[75vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://static.readdy.ai/image/afbb870fba5009b6251518206686e578/71bc3ab39195e50837b2b21906e6aac6.png"
            alt="Professional web design services workspace"
            className="w-full h-full object-cover object-center"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        {/* No Upfront Payment Badge */}
        <div className="absolute top-32 right-12 z-20 animate-pulse">
          <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white px-6 py-3 rounded-2xl shadow-2xl transform rotate-3 hover:rotate-0 transition-all duration-300">
            <div className="flex items-center gap-2">
              <i className="ri-gift-line text-2xl"></i>
              <div>
                <div className="font-bold text-lg whitespace-nowrap">No Upfront Payment</div>
                <div className="text-xs opacity-90">Pay After Approval</div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 drop-shadow-lg" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Web Design Services
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-10 drop-shadow-lg max-w-3xl mx-auto">
            Custom websites tailored to your business needs with transparent pricing and full ownership
          </p>
          
          {/* CTA Buttons - Moved to bottom */}
          <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 flex flex-wrap gap-4 justify-center" style={{ bottom: '-2rem' }}>
            <a
              href="/booking"
              className="group relative px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-calendar-check-line text-xl"></i>
              Get Started
            </a>
            <a
              href="/pricing"
              className="group relative px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-xl border-2 border-white/30 hover:bg-white/20 hover:scale-105 transition-all duration-300 flex items-center gap-2 whitespace-nowrap"
            >
              <i className="ri-price-tag-3-line text-xl"></i>
              View Pricing
            </a>
          </div>
        </div>
      </section>

      {/* AI Search Optimization Block - Moved After Hero */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <AISearchOptimization
          title="Comprehensive Web Design Services"
          summary="From custom website design to SEO optimization, e-commerce solutions, and ongoing maintenance - we offer complete web design services for businesses of all sizes. All services include transparent pricing and full ownership."
          keyPoints={[
            "Custom Website Design - Tailored to your brand and business goals",
            "SEO Optimization - Get found on Google with proper optimization",
            "E-commerce Solutions - Sell online with integrated shopping carts",
            "Website Maintenance - Keep your site secure and up-to-date",
            "Mobile-Responsive Design - Perfect on all devices"
          ]}
          entities={[
            {
              name: "Custom Website Design",
              type: "Service",
              description: "Fully customized website design tailored to your business needs. Includes brand integration, custom layouts, and professional imagery."
            },
            {
              name: "SEO Optimization",
              type: "Service",
              description: "Search engine optimization to help your website rank higher on Google. Includes keyword research, on-page optimization, and technical SEO."
            },
            {
              name: "E-commerce Solutions",
              type: "Service",
              description: "Complete online store setup with shopping cart, payment processing, inventory management, and order tracking."
            }
          ]}
          faqs={[
            {
              question: "What's included in custom website design?",
              answer: "Custom design includes brand integration, custom layouts, professional imagery, mobile responsiveness, contact forms, and SEO basics. You get a fully functional, professional website."
            },
            {
              question: "How does SEO optimization work?",
              answer: "We optimize your website structure, content, meta tags, images, and technical elements to help you rank higher on Google. This includes keyword research and on-page optimization."
            },
            {
              question: "Can you integrate e-commerce into my existing site?",
              answer: "Yes! We can add e-commerce functionality to existing websites or build a new online store from scratch. Includes payment processing and inventory management."
            }
          ]}
        />
      </div>

      {/* Website Design Solutions */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <div 
                key={index}
                className="relative bg-white p-8 rounded-2xl border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#3FA9F5] text-white px-6 py-2 rounded-full text-sm font-bold uppercase tracking-wider">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="w-16 h-16 bg-gradient-to-br from-[#0d3b66] to-[#3FA9F5] rounded-xl flex items-center justify-center mb-6 shadow-lg">
                  <i className={`${pkg.icon} text-3xl text-white`}></i>
                </div>
                
                <h3 className="text-2xl font-bold text-[#0d3b66] mb-2">{pkg.title}</h3>
                <p className="text-base text-[#3FA9F5] font-semibold mb-4">{pkg.pages}</p>
                <p className="text-sm text-gray-600 leading-relaxed mb-6">{pkg.description}</p>
                
                <ul className="space-y-3">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <i className="ri-check-line text-lg text-[#3FA9F5] flex-shrink-0 mt-0.5"></i>
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0d3b66] mb-4">What's Included in Every Website</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              All our website packages come with essential features to help your business grow online
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {included.map((item, index) => (
              <div 
                key={index}
                className="text-center p-6 rounded-xl hover:bg-gray-50 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#0d3b66] to-[#3FA9F5] rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <i className={`${item.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-base font-bold text-[#0d3b66] mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0d3b66] mb-4">Our Website Design Process</h2>
            <p className="text-lg text-gray-600">We make getting a new website simple and stress-free</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {process.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-[#0d3b66] to-[#3FA9F5] rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <i className={`${step.icon} text-2xl text-white`}></i>
                </div>
                
                <div className="mb-3">
                  <span className="text-3xl font-bold text-[#3FA9F5] opacity-40">{step.number}</span>
                </div>
                <h3 className="text-lg font-bold text-[#0d3b66] mb-2">{step.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0d3b66] mb-4">Industries We Serve</h2>
            <p className="text-lg text-gray-600">
              We design websites for all types of small and medium-sized businesses
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {industries.map((industry, index) => (
              <div 
                key={index}
                className="bg-gray-50 rounded-xl p-6 flex items-center gap-4 hover:bg-white hover:shadow-lg transition-all duration-300 group"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#0d3b66] to-[#3FA9F5] rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300">
                  <i className={`${industry.icon} text-xl text-white`}></i>
                </div>
                <p className="text-base font-semibold text-[#0d3b66]">{industry.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0d3b66] mb-4">Why Choose BusinessWebsitePro.com?</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {reasons.map((reason, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#0d3b66] to-[#3FA9F5] rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <i className={`${reason.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-base font-bold text-[#0d3b66] mb-2">{reason.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{reason.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
